import { useState, useEffect, useMemo, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import * as d3 from 'd3'; // Import d3 for force simulation
import AiInsightsRiskCard from './AiInsightsRiskCard';
import GraphEmptyState from './GraphEmptyState';
import SimpleGraphControls from './SimpleGraphControls';
import { createClusters, expandCluster } from '../utils/NodeCluster';
import simpleDrawNode from '../utils/SimpleNodeRenderer';
import profile360Service from '../services/profile360Service';
// Example color map for node types
const colorMap = {
  Department: '#1abc9c',
  Location:   '#2ecc71',
  Application:'#3498db',
  User:       '#9b59b6',
  Role:       '#f1c40f',
  Team:       '#e67e22',
  Resource:   '#e74c3c',
  Company:    '#95a5a6',
};

// Helper to check if node is a "User" by presence of user-like properties
const isUserNode = (node) => {
  if (!node || !node.properties) return false;
  const { full_name, jobTitle, department, email, createdDateTime } = node.properties;
  return Boolean(full_name || jobTitle || department || email || createdDateTime);
};

// Helper to extract initials for an avatar circle
const getInitials = (fullName = '') => {
  const names = fullName.trim().split(' ');
  if (!names[0]) return '';
  let initials = names[0].charAt(0).toUpperCase();
  if (names.length > 1) {
    initials += names[names.length - 1].charAt(0).toUpperCase();
  }
  return initials;
};

// Compose a human-friendly location string from various possible fields
const computeLocationDisplay = (props = {}) => {
  if (!props) return '';
  // Prefer precomposed fields if present
  const pre = props.location || props.officeLocation || props.office_location;
  if (pre && typeof pre === 'string') return pre;
  // Build from parts
  const city = props.city || props.town || '';
  const region = props.state || props.region || '';
  const country = props.country || props.countryOrRegion || props.country_or_region || '';
  const parts = [city, region, country].filter(Boolean);
  return parts.join(', ');
};


// Fetch user access summary from backend API
async function fetchUserAccessSummary(node) {
  try {
    // Extract email from node properties
    const email = node.properties?.email || node.properties?.user_email || '';

    if (!email) {
      console.warn('No email found for user node:', node);
      return { applications: 0, roles: 0, resources: 0 };
    }

    // Use the initialized profile360Service which has authentication
    const response = await profile360Service.apiService.getUserAccessSummary(email);

    if (!response.success) {
      console.error('Failed to fetch user access summary:', response.error);
      return { applications: 0, roles: 0, resources: 0 };
    }

    // Extract the data from the API response
    const data = response.data;

    // The API returns data nested under 'user_summary'
    const userSummary = data.user_summary || data;

    return {
      applications: userSummary.applications || 0,
      roles: userSummary.roles || 0,
      resources: userSummary.resources || 0
    };

  } catch (error) {
    console.error('Error fetching user access summary:', error);
    return { applications: 0, roles: 0, resources: 0 };
  }
}



const GraphView = ({
  filteredGraphData,
  filters,
  setFilters,
  graphRef,
  selectedNode,
  setSelectedNode,
  handleNodeClick: parentHandleNodeClick,
  loading,
  nlQuery,
  currentUserEmail
}) => {
  // State to store user access summary and UI state
  const [userAccessSummary, setUserAccessSummary] = useState(null);
  const [filtersMinimized, setFiltersMinimized] = useState(false);
  const [profileTab, setProfileTab] = useState('overview');

  // State to track the focused node ID (for highlighting connections)
  const [focusedNodeId, setFocusedNodeId] = useState(null);

  // Debug: Log focus state changes
  useEffect(() => {
    console.log('🎯 Focus state changed:', focusedNodeId);
  }, [focusedNodeId]);

  // State to prevent single-click interference with double-click
  const [recentDoubleClick, setRecentDoubleClick] = useState(false);

  // State to track selected link for showing details
  const [selectedLink, setSelectedLink] = useState(null);
  // State to track hovered link and mouse position for tooltip
  const [hoveredLink, setHoveredLink] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });



  // State for graph visualization enhancements
  const [enableClustering, setEnableClustering] = useState(true); // Enable clustering by default
  const [forceCluster, setForceCluster] = useState(false); // Track when clustering is manually forced
  // Using a fixed threshold for clustering
  const [nodeThreshold] = useState(20); // Threshold for when to apply clustering
  const [originalGraphData, setOriginalGraphData] = useState(null); // Store original graph data for expanding clusters

  // Reset profile tab when selection changes
  useEffect(() => {
    setProfileTab('overview');
  }, [selectedNode]);

  // Store original graph data when it changes
  useEffect(() => {
    if (filteredGraphData && filteredGraphData.nodes && filteredGraphData.nodes.length > 0) {
      setOriginalGraphData(filteredGraphData);
    }
  }, [filteredGraphData]);

  // Check if clustering is actually being applied
  const isClusteringActive = useMemo(() => {
    return enableClustering &&
           originalGraphData &&
           (originalGraphData.nodes.length > nodeThreshold || forceCluster);
  }, [enableClustering, originalGraphData, nodeThreshold, forceCluster]);

  // Apply clustering if enabled and node count exceeds threshold or if manually forced
  const displayData = useMemo(() => {
    // Always ensure we have a safe fallback structure
    const safeGraphData = filteredGraphData || { nodes: [], links: [] };

    console.log('🔗 GraphView displayData - Input filteredGraphData:', filteredGraphData);
    console.log('🔗 GraphView displayData - Links count:', safeGraphData.links?.length || 0);
    console.log('🔗 GraphView displayData - Sample links:', safeGraphData.links?.slice(0, 3));

    if (!isClusteringActive) {
      console.log('🔗 GraphView displayData - Returning safeGraphData (clustering not active)');
      return safeGraphData;
    }
    // Pass filters to createClusters to properly handle filtered nodes in clusters
    const clusteredData = createClusters(originalGraphData, nodeThreshold, colorMap, forceCluster, filters);
    console.log('🔗 GraphView displayData - Clustered data links:', clusteredData?.links?.length || 0);
    return clusteredData || safeGraphData;
  }, [filteredGraphData, originalGraphData, isClusteringActive, nodeThreshold, forceCluster, filters]);

  // Arrange nodes by color/type around a ring and set target positions for forces
  useEffect(() => {
    if (!displayData || !displayData.nodes || displayData.nodes.length === 0) return;

    try {
      const nodes = (displayData.nodes || []).filter(n => !n.isFiltered);

      // Identify center nodes (User nodes) that should stay near the middle
      const centerNodes = nodes.filter(n => (n.labels || []).includes('User') || isUserNode(n));
      const others = nodes.filter(n => !centerNodes.includes(n));

      // Group remaining nodes by primary label present in colorMap
      const groups = {};
      for (const n of others) {
        const labels = n.labels || [];
        // Find the label that determines the node's color (matches colorMap)
        const colorLabel = labels.find(l => colorMap[l]) || 'Other';
        if (!groups[colorLabel]) groups[colorLabel] = [];
        groups[colorLabel].push(n);

        // Debug logging to see what's happening
        console.log(`Node: ${n.name}, Labels: [${labels.join(', ')}], ColorLabel: ${colorLabel}`);
      }
      // Order groups alphabetically to keep layout stable even as label set changes
      const groupOrder = Object.keys(groups).sort((a, b) => a.localeCompare(b));
      console.log('Groups:', Object.keys(groups), 'Order:', groupOrder);

      // Flatten nodes in the order of groupOrder (unused groups appended alphabetically)
      const ordered = [];
      for (const key of groupOrder) {
        if (groups[key]) {
          groups[key].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          ordered.push(...groups[key]);
          delete groups[key];
        }
      }
      for (const key of Object.keys(groups).sort()) {
        groups[key].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        ordered.push(...groups[key]);
      }

      const total = ordered.length;
      if (total > 0) {
        // Compute a radius that scales slightly with node count
        const R = Math.min(520, 180 + total * 18);
        ordered.forEach((node, i) => {
          const angle = (2 * Math.PI * i) / total;
          node.__targetX = R * Math.cos(angle);
          node.__targetY = R * Math.sin(angle);
          // Provide an initial position close to target if not set
          if (typeof node.x !== 'number' || typeof node.y !== 'number') {
            node.x = node.__targetX + (Math.random() - 0.5) * 20;
            node.y = node.__targetY + (Math.random() - 0.5) * 20;
          }
        });
      }

      // Place center nodes in a small inner ring (or exactly at center if single)
      const c = centerNodes.length;
      if (c === 1) {
        const n = centerNodes[0];
        n.__targetX = 0;
        n.__targetY = 0;
        if (typeof n.x !== 'number' || typeof n.y !== 'number') {
          n.x = 0; n.y = 0;
        }
      } else if (c > 1) {
        const rInner = 140;
        centerNodes.forEach((node, i) => {
          const angle = (2 * Math.PI * i) / c;
          node.__targetX = rInner * Math.cos(angle);
          node.__targetY = rInner * Math.sin(angle);
          if (typeof node.x !== 'number' || typeof node.y !== 'number') {
            node.x = node.__targetX;
            node.y = node.__targetY;
          }
        });
      }
    } catch {}
  }, [displayData]);


  // Helper function to check if a link connects to filtered nodes
  const isLinkConnectedToFilteredNode = useCallback((link) => {
    if (!link || !displayData || !displayData.nodes) return false;

    const sourceNode = typeof link.source === 'object' ? link.source : displayData.nodes.find(n => n.id === link.source);
    const targetNode = typeof link.target === 'object' ? link.target : displayData.nodes.find(n => n.id === link.target);

    return (sourceNode && sourceNode.isFiltered) || (targetNode && targetNode.isFiltered);
  }, [displayData]);

  // Helper function to normalize node IDs for consistent comparison
  const normalizeId = useCallback((id) => {
    if (id === null || id === undefined) return '';
    return id.toString().trim();
  }, []);

  // Helper function to check if a node is connected to the focused node
  const isNodeConnectedToFocused = useCallback((node) => {
    try {
      // If no focused node ID or no node to check, return false
      if (!focusedNodeId || !node || !displayData || !displayData.links) {
        console.log('🎯 isNodeConnectedToFocused: early return', { focusedNodeId, hasNode: !!node, hasDisplayData: !!displayData });
        return false;
      }

      // Normalize node ID for comparison
      const nodeId = normalizeId(node.id);

      // If this is the focused node itself, return true
      if (nodeId === focusedNodeId) {
        return true;
      }

      // Check if there's a link between this node and the focused node
      return (displayData.links || []).some(link => {
        try {
          // Handle both object references and direct IDs
          let sourceId = link.source;
          let targetId = link.target;

          // Convert object references to IDs
          if (typeof sourceId === 'object' && sourceId !== null) {
            sourceId = normalizeId(sourceId.id);
          } else {
            sourceId = normalizeId(sourceId);
          }

          if (typeof targetId === 'object' && targetId !== null) {
            targetId = normalizeId(targetId.id);
          } else {
            targetId = normalizeId(targetId);
          }

          // Check for connection in either direction
          return (sourceId === nodeId && targetId === focusedNodeId) ||
                 (sourceId === focusedNodeId && targetId === nodeId);
        } catch (err) {
          return false;
        }
      });
    } catch (err) {
      return false;
    }
  }, [focusedNodeId, displayData, normalizeId]);

  // Add helper methods to the isNodeConnectedToFocused function
  // These are used by the node renderer to determine focus state
  isNodeConnectedToFocused.isFocusedNode = useCallback((node) => {
    if (!node || !focusedNodeId) return false;
    return normalizeId(node.id) === focusedNodeId;
  }, [focusedNodeId, normalizeId]);

  isNodeConnectedToFocused.hasFocusedNode = useCallback(() => {
    return !!focusedNodeId;
  }, [focusedNodeId]);

  // Helper function to check if a link is connected to the focused node
  const isLinkConnectedToFocused = useCallback((link) => {
    try {
      if (!focusedNodeId || !link || !displayData) return false;

      // Handle both object references and direct IDs for source
      let sourceId = link.source;
      if (typeof sourceId === 'object' && sourceId !== null) {
        sourceId = normalizeId(sourceId.id);
      } else {
        sourceId = normalizeId(sourceId);
      }

      // Handle both object references and direct IDs for target
      let targetId = link.target;
      if (typeof targetId === 'object' && targetId !== null) {
        targetId = normalizeId(targetId.id);
      } else {
        targetId = normalizeId(targetId);
      }

      // Check if either end of the link is the focused node
      return sourceId === focusedNodeId || targetId === focusedNodeId;
    } catch (err) {
      return false;
    }
  }, [focusedNodeId, displayData, normalizeId]);

  // Add helper method to check if we have a focused node
  isLinkConnectedToFocused.hasFocusedNode = useCallback(() => {
    return !!focusedNodeId;
  }, [focusedNodeId]);

  // Helper function to safely update graph data
  const updateGraphData = useCallback((newData) => {
    if (!graphRef.current) {
      return;
    }

    try {
      if (typeof graphRef.current.graphData === 'function') {
        // Standard way to update graph data
        graphRef.current.graphData(newData);
      } else {
        // Force re-render with new data by updating state
        const forceGraphEl = document.querySelector('.force-graph-container');
        if (forceGraphEl) {
          // Trigger a re-render
          forceGraphEl.dispatchEvent(new CustomEvent('data-update', { detail: newData }));
        }
      }
    } catch (err) {
      // Silently handle graph update errors
    }
  }, [graphRef]);

  // Custom function to center the graph based on canvas dimensions
  const centerGraph = useCallback(() => {
    if (!graphRef.current || !displayData || !displayData.nodes || displayData.nodes.length === 0) {
      return;
    }

    try {
      // Get the canvas element and its dimensions
      const graphContainer = document.querySelector('.graph-container');
      const canvas = document.querySelector('.graph-container canvas');
      if (!canvas || !graphContainer) {
        return;
      }


      // Calculate the bounding box of all visible nodes
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      let visibleNodeCount = 0;

      (displayData.nodes || []).forEach(node => {
        // Skip filtered nodes
        if (node.isFiltered) return;

        if (node.x !== undefined && node.y !== undefined) {
          minX = Math.min(minX, node.x);
          maxX = Math.max(maxX, node.x);
          minY = Math.min(minY, node.y);
          maxY = Math.max(maxY, node.y);
          visibleNodeCount++;
        }
      });

      // If no visible nodes, return
      if (visibleNodeCount === 0) {
        return;
      }

      // Get the current camera position
      graphRef.current.cameraPosition();

      // Use a gentler approach to center the graph
      if (typeof graphRef.current.zoomToFit === 'function') {
        // Use zoomToFit with a longer duration for smoother animation
        graphRef.current.zoomToFit(800, 100);
      } else if (typeof graphRef.current.centerAt === 'function') {
        // Fallback to centerAt with gentle animation
        graphRef.current.centerAt(0, 0, 800);

        // Then apply zoom with a gentle animation
        setTimeout(() => {
          if (typeof graphRef.current.zoom === 'function') {
            // Calculate a moderate zoom level
            const containerRect = graphContainer.getBoundingClientRect();
            const minDimension = Math.min(containerRect.width, containerRect.height);
            const nodeArea = Math.max(maxX - minX, maxY - minY);
            const zoom = Math.min(minDimension / (nodeArea * 1.5), 1.5);

            // Apply zoom with longer duration for smoother animation
            graphRef.current.zoom(zoom, 800);
          }
        }, 850);
      }
    } catch (err) {
      // Fallback to basic zoomToFit
      if (graphRef.current && typeof graphRef.current.zoomToFit === 'function') {
        graphRef.current.zoomToFit(600, 100); // Use larger padding in fallback
      }
    }
  }, [displayData, graphRef]);

  // Handle link click to show link details (toggle on/off for multiple selection)
  const handleLinkClick = useCallback((link, event) => {
    // Prevent event propagation to avoid conflicts
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    // Convert selectedLink to array if it's not already
    const currentSelectedLinks = Array.isArray(selectedLink) ? selectedLink : (selectedLink ? [selectedLink] : []);

    // Check if this link is already selected
    const linkIndex = currentSelectedLinks.findIndex(selectedLinkItem => selectedLinkItem === link);

    if (linkIndex !== -1) {
      // Link is already selected, remove it from selection
      const newSelectedLinks = currentSelectedLinks.filter(selectedLinkItem => selectedLinkItem !== link);
      // Link removed from selection
      setSelectedLink(newSelectedLinks.length > 0 ? newSelectedLinks : null);
    } else {
      // Link is not selected, add it to selection
      const newSelectedLinks = [...currentSelectedLinks, link];
      setSelectedLink(newSelectedLinks);
      // Clear node selection when link is selected
      setSelectedNode(null);
    }
  }, [selectedLink, setSelectedNode]);

  // Handle node double-click for focus functionality
  const handleInternalNodeDoubleClick = useCallback(async (node) => {
    // Skip interaction with filtered nodes
    if (node.isFiltered) {
      return;
    }

    console.log('🎯 Node double-clicked for focus:', node);

    // Set flag to prevent single-click interference
    setRecentDoubleClick(true);
    setTimeout(() => setRecentDoubleClick(false), 300); // Clear flag after 300ms

    // Set this node as the focused node for highlighting its connections
    // If the same node is double-clicked again, clear the focus
    try {
      const clickedNodeId = normalizeId(node.id);
      const isCurrentlyFocused = focusedNodeId && clickedNodeId === focusedNodeId;



      if (isCurrentlyFocused) {
        // Clear focus with a slight delay to ensure proper state update
        console.log('🎯 Same node double-clicked, clearing focus');
        setTimeout(() => {
          console.log('🎯 CLEARING FOCUS: Same node double-clicked');
          setFocusedNodeId(null);
        }, 50);

        // When clearing focus, don't call the parent handler
        // The user's intention is to exit focus mode, not expand the node
        console.log('🎯 Focus cleared, not calling parent handler (user wants to exit focus mode)');
      } else {
        // Store just the ID, not the whole node object
        console.log('🎯 Setting focus to node:', clickedNodeId);
        setFocusedNodeId(clickedNodeId);
        // When setting focus, skip the parent handler to avoid conflicts
        console.log('🎯 Skipping parent double-click handler to avoid conflicts with focus mode');
      }
    } catch (err) {
      console.log('🎯 Error in focus logic, clearing focus:', err);
      // In case of error, clear the focus to avoid stuck states
      console.log('🎯 CLEARING FOCUS: Error in focus logic');
      setFocusedNodeId(null);
    }
  }, [focusedNodeId, setFocusedNodeId, normalizeId, setRecentDoubleClick]);

  // On node click, set the selected node and fetch access summary if applicable
  const handleNodeClick = useCallback(async (node) => {
    // Skip interaction with filtered nodes
    if (node.isFiltered) {
      return;
    }

    // Clear selected link when node is clicked
    setSelectedLink(null);

    // If clicking on a cluster node, expand it
    if (node.isCluster && originalGraphData) {
      // Check if the node has child IDs
      if (!node.childNodeIds || node.childNodeIds.length === 0) {
        return;
      }

      try {
        // Create expanded data
        const expandedData = expandCluster(displayData, node, originalGraphData);

        // Update the graph with expanded data
        if (graphRef.current) {

          // Force a complete refresh of the graph
          const tempData = {nodes: [], links: []};
          updateGraphData(tempData);

          // Short delay before adding the new data
          setTimeout(() => {
            updateGraphData(expandedData);

            // Center the graph on the expanded area after a short delay
            setTimeout(() => {
              centerGraph();
            }, 300);
          }, 50);
        }
      } catch (err) {
        // Silently handle cluster expansion errors
      }
      return;
    }
    setSelectedNode(node);

    // Call parent's handleNodeClick if provided (for updating currentUserEmail)
    if (parentHandleNodeClick) {
      parentHandleNodeClick(node);
    }

    // Note: We can't check selectedNode immediately after setSelectedNode
    // because React state updates are asynchronous

    if (isUserNode(node)) {
      try {
        const summary = await fetchUserAccessSummary(node);
        setUserAccessSummary(summary);
      } catch (err) {
        setUserAccessSummary({ applications: 0, roles: 0, resources: 0 });
      }
    } else {
      setUserAccessSummary(null);
    }
  }, [originalGraphData, displayData, setSelectedNode, setUserAccessSummary, graphRef, updateGraphData, centerGraph, parentHandleNodeClick]);





  // Force the graph to center when the component mounts with smoother movement
  useEffect(() => {
    // Force the d3 forces to be properly centered
    if (graphRef.current && typeof graphRef.current.d3Force === 'function') {
      // Update the center force to use the middle of the container with moderate strength
      graphRef.current.d3Force('center', d3.forceCenter(0, 0).strength(0.05)); // Gentle centering force

      // Add a radial force to push nodes toward the center with moderate strength
      graphRef.current.d3Force('radial', d3.forceRadial(0).strength(0.03)); // Gentle radial force

      // Disable x and y forces that might be pulling nodes away from center
      graphRef.current.d3Force('x', null);
      graphRef.current.d3Force('y', null);

      // Add gentle forces that guide nodes toward their color-ordered target positions
      setTimeout(() => {
        graphRef.current.d3Force('x', d3.forceX(d => d.__targetX || 0).strength(0.08));
        graphRef.current.d3Force('y', d3.forceY(d => d.__targetY || 0).strength(0.08));
      }, 100);

      // Adjust the alpha (simulation speed) parameters for smoother movement
      if (typeof graphRef.current.d3AlphaTarget === 'function') {
        // Set a very low alpha target for smoother stabilization
        graphRef.current.d3AlphaTarget(0);
      }

      if (typeof graphRef.current.d3AlphaDecay === 'function') {
        // Slower decay means smoother movement but longer to stabilize
        graphRef.current.d3AlphaDecay(0.01); // Default is 0.0228
      }

      if (typeof graphRef.current.d3VelocityDecay === 'function') {
        // Higher velocity decay means more damping (less oscillation)
        graphRef.current.d3VelocityDecay(0.4); // Default is 0.4, increase for more damping
      }
    }
  }, [graphRef]);

  // Reset view to original state
  const handleResetView = () => {
    if (graphRef.current) {
      // If clustering is enabled, reset to clustered view
      if (enableClustering && originalGraphData) {
        const clusteredData = createClusters(originalGraphData, nodeThreshold, colorMap, forceCluster, filters);
        updateGraphData(clusteredData);
      } else if (originalGraphData) {
        // Otherwise reset to original data
        updateGraphData(originalGraphData);
      }

      // Clear selected node and focused node
      setSelectedNode(null);
      setUserAccessSummary(null);
      console.log('🎯 CLEARING FOCUS: Filter changed (1)');
      setFocusedNodeId(null);

      // Use our custom centering function after a short delay to allow data to update
      setTimeout(() => centerGraph(), 100);
    }
  };

  // Toggle clustering on/off
  const handleToggleClustering = () => {
    const newClusteringState = !enableClustering;
    setEnableClustering(newClusteringState);

    // If turning clustering on, force it even for small graphs
    if (newClusteringState) {
      setForceCluster(true);
    } else {
      setForceCluster(false);
    }

    if (graphRef.current && originalGraphData) {
      if (newClusteringState) {
        // Apply clustering (force it even for small graphs)
        const clusteredData = createClusters(originalGraphData, nodeThreshold, colorMap, true, filters);
        updateGraphData(clusteredData);
      } else {
        // Remove clustering
        updateGraphData(originalGraphData);
      }

      // Clear selected node and focused node
      setSelectedNode(null);
      setUserAccessSummary(null);
      console.log('🎯 CLEARING FOCUS: Filter changed (2)');
      setFocusedNodeId(null);

      // Use our custom centering function after a short delay to allow data to update
      setTimeout(() => centerGraph(), 100);
    }
  };

  // Auto-center the graph when data is loaded
  useEffect(() => {
    // Only proceed if we have graph data and the graph is rendered
    if (displayData && displayData.nodes && displayData.nodes.length > 0 && graphRef.current) {
      // Wait for the graph to stabilize before centering
      const timer = setTimeout(() => {
        centerGraph();
      }, 500); // Wait for graph to stabilize

      return () => clearTimeout(timer);
    }
  }, [displayData, graphRef, centerGraph]); // Re-run when display data changes

  // Add window resize listener to recenter graph when window size changes
  useEffect(() => {
    const handleResize = () => {
      if (displayData && displayData.nodes && displayData.nodes.length > 0) {
        // Add a small delay to ensure the canvas has resized
        setTimeout(() => centerGraph(), 200);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [displayData, centerGraph]);

  // Recenter graph when filters change
  useEffect(() => {
    if (displayData && displayData.nodes && displayData.nodes.length > 0) {
      // Add a small delay to allow the graph to update after filter changes
      setTimeout(() => centerGraph(), 300);
    }
  }, [filters, centerGraph, displayData]);

  // Reset focus state when graph data changes significantly
  useEffect(() => {
    // Clear focused node when graph data changes to avoid stale references
    // Note: We don't include focusedNodeId in deps to avoid clearing focus when it's set
    setFocusedNodeId(prevFocusedNodeId => {
      if (prevFocusedNodeId) {
        console.log('🎯 CLEARING FOCUS: Graph data changed');
        return null;
      }
      return prevFocusedNodeId;
    });
  }, [filteredGraphData, displayData]);

  // Add comprehensive click and mouse handlers for better edge/node detection
  useEffect(() => {
    // Only add the handler if we have graph data
    if (displayData && displayData.nodes && displayData.nodes.length > 0) {
      // Find the canvas element
      const canvas = document.querySelector('.graph-container canvas');
      if (!canvas) return;

      // Comprehensive click handler for both nodes and edges
      const canvasClickHandler = (event) => {
        if (!graphRef.current || !displayData) return;

        // Skip processing if we recently had a double-click to avoid interference
        if (recentDoubleClick) {
          console.log('🎯 Skipping click processing due to recent double-click');
          return;
        }

        console.log('🎯 Canvas clicked');

        // Get mouse coordinates relative to canvas
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Convert screen coordinates to graph coordinates
        const graphCoords = graphRef.current.screen2GraphCoords(x, y);

        // First, check for node clicks
        let closestNode = null;
        let closestNodeDistance = Infinity;

        (displayData.nodes || []).forEach(node => {
          // Skip nodes that are filtered or don't have coordinates
          if (!node.x || !node.y || node.isFiltered) return;

          // Calculate distance to node
          const distance = Math.sqrt(
            Math.pow(graphCoords.x - node.x, 2) +
            Math.pow(graphCoords.y - node.y, 2)
          );

          // Use the stored radius from the renderer if available
          const nodeRadius = node.__radius || 40;

          // If within node radius and closer than previous matches
          if (distance < nodeRadius && distance < closestNodeDistance) {
            closestNode = node;
            closestNodeDistance = distance;
          }
        });

        // If we found a node, handle the node click and return
        if (closestNode) {
          console.log('🎯 Node detected, handling node click');
          handleNodeClick(closestNode, event);
          return;
        }

        // If no node was clicked, check for edge clicks
        console.log('🎯 No node found, checking for edges...');
        let closestLink = null;
        let closestLinkDistance = Infinity;
        const linkClickThreshold = 15; // pixels tolerance for edge clicking

        (displayData.links || []).forEach(link => {
          // Skip filtered links or links without proper source/target
          if (link.isFiltered || isLinkConnectedToFilteredNode(link)) return;

          const source = typeof link.source === 'object' ? link.source : null;
          const target = typeof link.target === 'object' ? link.target : null;

          if (!source || !target || source.x === undefined || target.x === undefined) return;

          // Calculate distance from click point to the line segment
          const A = graphCoords.x - source.x;
          const B = graphCoords.y - source.y;
          const C = target.x - source.x;
          const D = target.y - source.y;

          const dot = A * C + B * D;
          const lenSq = C * C + D * D;

          if (lenSq === 0) return; // source and target are the same point

          let param = dot / lenSq;

          // Clamp parameter to line segment
          param = Math.max(0, Math.min(1, param));

          const closestX = source.x + param * C;
          const closestY = source.y + param * D;

          const distance = Math.sqrt(
            Math.pow(graphCoords.x - closestX, 2) +
            Math.pow(graphCoords.y - closestY, 2)
          );

          // Convert threshold to graph coordinates
          const scale = graphRef.current.zoom() || 1;
          const graphThreshold = linkClickThreshold / scale;

          console.log(`🔗 Checking link ${link.type || 'CONNECTED'}: distance=${distance.toFixed(2)}, threshold=${graphThreshold.toFixed(2)}`);

          if (distance < graphThreshold && distance < closestLinkDistance) {
            closestLink = link;
            closestLinkDistance = distance;
            console.log(`🔗 Found closer link: ${link.type || 'CONNECTED'}`);
          }
        });

        // If we found a link, handle the link click
        if (closestLink) {
          console.log('🔗 Edge detected, handling link click');
          handleLinkClick(closestLink, event);
        } else {
          console.log('🎯 No edge found, this is a background click');
          // Handle background click - clear selections
          if (focusedNodeId) {
            console.log('🎯 CLEARING FOCUS: Background click');
            setFocusedNodeId(null);

          }
          if (selectedLink) {
            console.log('🎯 Clearing selected link');
            setSelectedLink(null);
          }
        }
      };

      // Mouse move handler for tooltip positioning
      const canvasMouseMoveHandler = (event) => {
        const rect = canvas.getBoundingClientRect();
        setMousePos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        });
      };

      // Double-click handler for focus functionality
      const canvasDoubleClickHandler = (event) => {
        if (!graphRef.current || !displayData) return;

        console.log('🎯 Canvas double-clicked');

        // Get mouse coordinates relative to canvas
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Convert screen coordinates to graph coordinates
        const graphCoords = graphRef.current.screen2GraphCoords(x, y);

        // Check for node double-clicks
        let closestNode = null;
        let closestNodeDistance = Infinity;

        (displayData.nodes || []).forEach(node => {
          // Skip nodes that are filtered or don't have coordinates
          if (!node.x || !node.y || node.isFiltered) return;

          // Calculate distance to node
          const distance = Math.sqrt(
            Math.pow(graphCoords.x - node.x, 2) +
            Math.pow(graphCoords.y - node.y, 2)
          );

          // Use the stored radius from the renderer if available
          const nodeRadius = node.__radius || 40;

          // If within node radius and closer than previous matches
          if (distance < nodeRadius && distance < closestNodeDistance) {
            closestNode = node;
            closestNodeDistance = distance;
          }
        });

        // If we found a node, handle the double-click
        if (closestNode) {
          console.log('🎯 Node double-click detected, calling handler');
          handleInternalNodeDoubleClick(closestNode, event);
        }
      };

      // Add the event handlers
      canvas.addEventListener('click', canvasClickHandler);
      canvas.addEventListener('dblclick', canvasDoubleClickHandler);
      canvas.addEventListener('mousemove', canvasMouseMoveHandler);

      // Clean up
      return () => {
        canvas.removeEventListener('click', canvasClickHandler);
        canvas.removeEventListener('dblclick', canvasDoubleClickHandler);
        canvas.removeEventListener('mousemove', canvasMouseMoveHandler);
      };
    }
  }, [displayData, graphRef, handleNodeClick, handleLinkClick, handleInternalNodeDoubleClick, isLinkConnectedToFilteredNode, focusedNodeId, selectedLink, setFocusedNodeId, setSelectedLink, recentDoubleClick]);

  // State for showing/hiding the clustering explanation
  const [showClusteringExplanation, setShowClusteringExplanation] = useState(true);

  // Show clustering explanation when clustering is actually active
  useEffect(() => {
    if (isClusteringActive) {
      // Show the explanation
      setShowClusteringExplanation(true);

      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => {
        setShowClusteringExplanation(false);
      }, 8000);

      return () => clearTimeout(timer);
    } else {
      // Hide explanation when clustering is not active
      setShowClusteringExplanation(false);
    }
  }, [isClusteringActive]);

  // Calculate node counts by type
  const getNodeCountsByType = useCallback(() => {
    if (!filteredGraphData || !filteredGraphData.nodes) return {};

    const counts = {};
    filteredGraphData.nodes.forEach(node => {
      if (node.labels && node.labels.length > 0) {
        node.labels.forEach(label => {
          counts[label] = (counts[label] || 0) + 1;
        });
      }
    });
    return counts;
  }, [filteredGraphData]);

  // Get node counts for display
  const nodeCounts = useMemo(() => getNodeCountsByType(), [getNodeCountsByType]);

  // Get only the node types that are present in the current graph
  const activeNodeTypes = useMemo(() => {
    return Object.keys(nodeCounts).filter(label => nodeCounts[label] > 0);
  }, [nodeCounts]);

  // Check if we should show empty state message
  const showEmptyState = !loading && (!filteredGraphData || !filteredGraphData.nodes || filteredGraphData.nodes.length === 0);

  return (
    <div className="row g-0" style={{ height: '75vh', maxHeight: '75vh', overflow: 'hidden' }}>
      {/* Left Column: Two separate cards for Filters and Legend - Only show when there's data */}
      {!showEmptyState && (
        <div className="col-12 col-md-2 border-end p-3 overflow-auto">
          {/* Combined Filter & Legend Card */}
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">FILTER & LEGEND</h6>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setFiltersMinimized(!filtersMinimized)}
              >
                {filtersMinimized ? '+' : '–'}
              </button>
            </div>
            {!filtersMinimized && (
              <div className="card-body p-2">
                <div className="filter-legend-container">
                  {/* Node Types Section */}
                  <div className="mb-3">
                    <h6 className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>NODE TYPES</h6>
                    {Object.entries(colorMap)
                      .filter(([label]) => activeNodeTypes.includes(label))
                      .map(([label, color]) => (
                      <div key={label} className="d-flex align-items-center mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          id={`filter-${label}`}
                          checked={filters[label] || false}
                          onChange={() => {
                            setFilters(prev => ({
                              ...prev,
                              [label]: !prev[label]
                            }));
                          }}
                        />
                        <span
                          className="rounded-circle d-inline-block me-2"
                          style={{ width: '12px', height: '12px', backgroundColor: color }}
                        />
                        <label className="form-check-label" htmlFor={`filter-${label}`}>
                          {label} {nodeCounts[label] ? `(${nodeCounts[label]})` : ''}
                        </label>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Center Column: ForceGraph Canvas */}
      <div
        className={
          showEmptyState
            ? "col-12 position-relative"
            : selectedNode || selectedLink
              ? "col-12 col-md-6 position-relative"
              : "col-12 col-md-8 position-relative"
        }
        style={{ overflow: 'hidden', height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <div className="graph-container"
          onMouseMove={(e) => {
            // Track mouse for tooltip positioning
            const rect = e.currentTarget.getBoundingClientRect();
            setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
        >
          <ForceGraph2D
            ref={graphRef}
            graphData={displayData}
            backgroundColor="#fafafa"
            nodeRelSize={4}
            nodeLabel={node => {
              if (node.isCluster) {
                let tooltip = `${node.fullName || node.name} (${node.childCount} nodes)`;
                if (node.description) tooltip += `\n${node.description}`;
                if (node.sampleNodes && node.sampleNodes.length > 0) {
                  tooltip += '\n\nSample nodes:';
                  node.sampleNodes.forEach((sample, i) => {
                    if (i < 5) tooltip += `\n• ${sample.name || sample.id}`;
                  });
                  if (node.childCount > node.sampleNodes.length) {
                    tooltip += `\n• ... and ${node.childCount - node.sampleNodes.length} more`;
                  }
                }
                tooltip += '\n\nClick to expand this cluster';
                return tooltip;
              } else {
                let tooltip = node.labels.join(', ');
                if (node.name) tooltip += `\n${node.name}`;
                tooltip += '\n\nDouble-click to expand connections';
                return tooltip;
              }
            }}
            linkLabel={link => {
              // Check if this link is in the selected links array
              const selectedLinks = Array.isArray(selectedLink) ? selectedLink : (selectedLink ? [selectedLink] : []);
              const isThisLinkSelected = selectedLinks.some(selectedLinkItem => selectedLinkItem === link);

              // Check if this link is connected to the focused node (for focus mode)
              const isConnectedToFocused = isLinkConnectedToFocused(link);
              const hasFocusedNode = isLinkConnectedToFocused.hasFocusedNode?.();

              // Debug logging removed for production

              // Show label if link is manually selected OR connected to focused node
              if (isThisLinkSelected || (hasFocusedNode && isConnectedToFocused)) {
                const linkType = link?.type || 'CONNECTED';
                return linkType.replace(/_/g, ' ');
              }
              return ''; // Hide labels for non-selected links
            }}
            linkHoverPrecision={8}
            linkDirectionalArrowLength={link => {
              if (link.isFiltered || isLinkConnectedToFilteredNode(link)) return 0;
              if (isLinkConnectedToFocused.hasFocusedNode?.()) {
                if (isLinkConnectedToFocused(link)) {
                  return link.type === 'CLUSTER_LINK' ? 0 : 8;
                } else {
                  return 0;
                }
              }
              return link.type === 'CLUSTER_LINK' ? 0 : 6;
            }}
            linkDirectionalParticles={0}
            linkWidth={link => {
              if (link.isFiltered || isLinkConnectedToFilteredNode(link)) return 0;
              if (isLinkConnectedToFocused.hasFocusedNode?.()) {
                if (isLinkConnectedToFocused(link)) {
                  // Connected edges get slightly thicker width
                  return 2.5;
                } else {
                  // Non-connected edges get thinner width
                  return 1.5;
                }
              }
              return 2.0; // Default thinner width for less clutter
            }}
            linkColor={link => {
              if (link.isFiltered || isLinkConnectedToFilteredNode(link)) return 'rgba(0,0,0,0)';

              // Apply opacity based on focus state
              if (isLinkConnectedToFocused.hasFocusedNode?.()) {
                if (isLinkConnectedToFocused(link)) {
                  // Connected edges get higher opacity
                  return 'rgba(158, 158, 158, 0.8)';
                } else {
                  // Non-connected edges get very low opacity
                  return 'rgba(158, 158, 158, 0.15)';
                }
              }

              // No focus - reduced opacity for less clutter
              return 'rgba(158, 158, 158, 0.4)';
            }}
            linkCurvature={0}
            linkLineDash={[]}
            onNodeClick={() => {}} // Disable built-in node click, we handle it manually
            onNodeDoubleClick={handleInternalNodeDoubleClick}
            onLinkClick={() => {}} // Disable built-in link click, we handle it manually
            onLinkHover={(link) => {
              setHoveredLink(link || null);
            }}
            onLinkRightClick={(link, event) => {
              event.preventDefault();
              handleLinkClick(link, event);
            }}
            // Draw link labels aligned with the edge with safeguards for readability
            linkCanvasObjectMode={() => 'after'}
            linkCanvasObject={(link, ctx, globalScale) => {
              const selectedLinks = Array.isArray(selectedLink) ? selectedLink : (selectedLink ? [selectedLink] : []);
              const isSelected = selectedLinks.some(selectedLinkItem => selectedLinkItem === link);

              // Check if this link is connected to the focused node (for focus mode)
              const isConnectedToFocused = isLinkConnectedToFocused(link);
              const hasFocusedNode = isLinkConnectedToFocused.hasFocusedNode?.();

              // Debug logging removed for production

              try {
                const start = typeof link.source === 'object' ? link.source : null;
                const end = typeof link.target === 'object' ? link.target : null;
                if (!start || !end || start.x === undefined || end.x === undefined) return;

                // Render labels when link is manually selected OR connected to focused node
                if (!isSelected && !(hasFocusedNode && isConnectedToFocused)) {
                  return;
                }

                const dx = end.x - start.x;
                const dy = end.y - start.y;
                const lengthWorld = Math.hypot(dx, dy);
                const lengthScreen = lengthWorld * globalScale;

                const label = (link.type || 'CONNECTED').replace(/_/g, ' ');
                const midX = (start.x + end.x) / 2;
                const midY = (start.y + end.y) / 2;
                const angle = Math.atan2(dy, dx);

                // Base font in screen pixels for selected edges
                const baseScreenPx = 13.2;
                const minScreenPx = 8.4;
                let fontWorldPx = baseScreenPx / globalScale; // convert to world units

                // Set preliminary font to measure text width
                ctx.save();
                ctx.font = `${fontWorldPx}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif`;
                let textWidthWorld = ctx.measureText(label).width;
                let textWidthScreen = textWidthWorld * globalScale;

                // Constrain label width to the visible portion of the link with padding
                const maxWidthScreen = Math.max(40, lengthScreen - 24); // keep margins from nodes
                if (textWidthScreen > maxWidthScreen) {
                  const scale = maxWidthScreen / textWidthScreen;
                  const targetScreenPx = Math.max(minScreenPx, baseScreenPx * scale);
                  fontWorldPx = targetScreenPx / globalScale;
                  ctx.font = `${fontWorldPx}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif`;
                  textWidthWorld = ctx.measureText(label).width;
                  textWidthScreen = textWidthWorld * globalScale;
                }

                // Offset the label slightly away from the edge to avoid overlap with the line
                const offsetScreen = 6; // px
                const offsetWorld = offsetScreen / globalScale;

                ctx.translate(midX, midY);
                ctx.rotate(angle);
                ctx.translate(0, -offsetWorld); // move perpendicular to the edge

                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Subtle white outline for contrast, constant ~2px on screen
                ctx.lineWidth = 2 / globalScale;
                ctx.strokeStyle = 'rgba(255,255,255,0.95)';
                ctx.fillStyle = '#424242';

                ctx.strokeText(label, 0, 0);
                ctx.fillText(label, 0, 0);
                ctx.restore();
              } catch {}
            }}
            nodeCanvasObjectMode={() => 'replace'}
            enableNodeDrag={false}
            enableZoomInteraction={true}
            enablePanInteraction={true}
            onBackgroundClick={() => {}} // Disable built-in background click, we handle it manually
            nodeCanvasObject={(node, ctx, globalScale) => {
              const getNodeColor = (node) => {
                for (let label of node.labels || []) {
                  if (colorMap[label]) return colorMap[label];
                }
                return '#3498db';
              };
              simpleDrawNode(node, ctx, globalScale, getNodeColor, null, isNodeConnectedToFocused);
            }}
            cooldownTicks={150}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.6}
            minZoom={0.1}
            maxZoom={5}
            warmupTicks={80}
            onEngineStop={() => {
              setTimeout(() => {
                if (graphRef.current && typeof graphRef.current.zoomToFit === 'function') {
                  graphRef.current.zoomToFit(800, 100);
                }
              }, 200);
            }}
            d3Force={{
              // Stronger repulsion force to prevent node overlap and clustering
              'charge': d3.forceManyBody().strength(-400).distanceMax(600),
              'center': d3.forceCenter(0, 0).strength(0.05),
              // Collision radius adjusted for smaller node sizes with extra spacing
              'collide': d3.forceCollide(60).strength(1.0).iterations(3),
            }}
          />

          {/* Hover tooltip for relationship type */}
          {hoveredLink && (
            <div
              style={{
                position: 'absolute',
                left: mousePos.x + 12,
                top: mousePos.y + 12,
                background: 'rgba(33,33,33,0.9)',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: 6,
                fontSize: 12,
                pointerEvents: 'none',
                zIndex: 5,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              {(hoveredLink.type || 'CONNECTED').replace(/_/g, ' ')}
            </div>
          )}


          {/* Add simple graph controls - only when there's data to display */}
          {!loading && displayData && displayData.nodes && displayData.nodes.length > 0 && (
            <SimpleGraphControls
              graphRef={graphRef}
              onResetView={handleResetView}
              onToggleClustering={handleToggleClustering}
              clusteringEnabled={isClusteringActive}
            />
          )}

          {/* Node count indicator */}
          {displayData && displayData.nodes && displayData.nodes.length > 0 && (
            <div className="node-count-indicator">
              {displayData.nodes.length} nodes / {(displayData.links || []).length} links
              {isClusteringActive && originalGraphData && (
                <span> (clustered from {originalGraphData.nodes.length} nodes)</span>
              )}
              {focusedNodeId && (
                <span className="ms-3 badge bg-warning text-dark">
                  Focused: {focusedNodeId}
                  <button
                    className="btn btn-sm btn-link text-dark p-0 ms-2"
                    onClick={() => setFocusedNodeId(null)}
                    style={{ fontSize: '10px', verticalAlign: 'middle' }}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Clustering explanation with auto-dismiss */}
          {showClusteringExplanation && isClusteringActive && (
            <div className="clustering-explanation fade-in-out">
              <div className="alert alert-info">
                <h6 className="mb-1"><i className="bi bi-info-circle me-2"></i>Clustering Active</h6>
                <p className="mb-0 small">
                  Nodes are grouped by type for better visualization. Click on any cluster to expand it and see individual nodes. Use the ⊞ button in the controls to toggle clustering on/off.
                </p>
                <button
                  className="dismiss-button"
                  onClick={() => setShowClusteringExplanation(false)}
                  aria-label="Dismiss explanation"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Empty state message overlay */}
        {showEmptyState && (
          <GraphEmptyState type={nlQuery && nlQuery.trim() !== '' ? 'noData' : 'initial'} />
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 10 }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Right Column: Node Details */}
      {selectedNode && (
        <div className="col-12 col-md-4 p-3 node-details-column">
          {isUserNode(selectedNode) ? (
            <>
              {/* Modern Profile Card */}
              <div className="card shadow-sm mb-3 profile-card">
                <div className="card-body py-3">
                  {/* Avatar + Name + Email */}
                  <div className="d-flex flex-column align-items-center text-center mb-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold overflow-hidden"
                      style={{ width: '64px', height: '64px', backgroundColor: '#9b59b6', fontSize: '1.2rem' }}
                    >
                      {selectedNode.properties.photoUrl ? (
                        <img src={selectedNode.properties.photoUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        getInitials(selectedNode.properties.name || selectedNode.properties.full_name || '')
                      )}
                    </div>
                    <h5 className="mb-0 mt-2" style={{ fontWeight: 700 }}>
                      {selectedNode.properties.name || selectedNode.properties.full_name || 'Unknown User'}
                    </h5>
                    {selectedNode.properties.email && (
                      <div className="text-muted small" style={{ wordBreak: 'break-all' }}>
                        {selectedNode.properties.email}
                      </div>
                    )}
                  </div>

                  {/* Lightweight Tabs */}
                  <ul className="nav nav-underline justify-content-center small mb-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${profileTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setProfileTab('overview')}
                      >
                        Overview
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${profileTab === 'access' ? 'active' : ''}`}
                        onClick={() => setProfileTab('access')}
                      >
                        Access
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${profileTab === 'activity' ? 'active' : ''}`}
                        onClick={() => setProfileTab('activity')}
                      >
                        Activity
                      </button>
                    </li>
                  </ul>

                  {/* Tab Content */}
                  {profileTab === 'overview' && (
                    <div className="row gx-3 gy-2 small">
                      {selectedNode.properties.jobTitle && (
                        <div className="col-6">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.72rem' }}>Title</div>
                          <div className="fw-semibold">{selectedNode.properties.jobTitle}</div>
                        </div>
                      )}
                      {selectedNode.properties.department && (
                        <div className="col-6">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.72rem' }}>Department</div>
                          <div className="fw-semibold">{selectedNode.properties.department}</div>
                        </div>
                      )}
                      {computeLocationDisplay(selectedNode.properties) && (
                        <div className="col-6">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.72rem' }}>Location</div>
                          <div className="fw-semibold">{computeLocationDisplay(selectedNode.properties)}</div>
                        </div>
                      )}
                      {selectedNode.properties.createdDateTime && (
                        <div className="col-6">
                          <div className="text-muted text-uppercase" style={{ fontSize: '0.72rem' }}>Created</div>
                          <div className="fw-semibold">{selectedNode.properties.createdDateTime}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {profileTab === 'access' && (
                    <div className="row g-0 text-center">
                      <div className="col-4 border-end">
                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#0d6efd' }}>
                          {userAccessSummary ? userAccessSummary.applications : '—'}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 500 }}>Applications</div>
                      </div>
                      <div className="col-4 border-end">
                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#0d6efd' }}>
                          {userAccessSummary ? userAccessSummary.roles : '—'}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 500 }}>Roles</div>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#0d6efd' }}>
                          {userAccessSummary ? userAccessSummary.resources : '—'}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 500 }}>Resources</div>
                      </div>
                    </div>
                  )}

                  {profileTab === 'activity' && (
                    <div className="small">
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Last Seen</span>
                        <span className="fw-semibold">{selectedNode.properties.lastSeen || '—'}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-muted">Status</span>
                        <span className="fw-semibold">{selectedNode.properties.status || 'Active'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>


              {/* AI Insights & Risk Card */}
              <AiInsightsRiskCard userEmail={currentUserEmail} />
            </>
          ) : (
            <div className="card shadow-sm">
              <div className="card-body">


                <h5 className="card-title mb-0">
                  {selectedNode.labels && selectedNode.labels.length > 0 ? selectedNode.labels[0] : 'Unknown Type'}
                </h5>
                <p className="text-muted mb-2">{(selectedNode.properties && selectedNode.properties.name) || selectedNode.name || 'Unknown Node'}</p>
                {selectedNode.properties && Object.keys(selectedNode.properties).length > 0 && (
                  <ul className="list-group list-group-flush mt-2">
                    {Object.entries(selectedNode.properties)
                      .filter(([key]) => key.toLowerCase() !== 'name')
                      .map(([key, value]) => (
                        <li className="list-group-item py-1" key={key}>
                          <strong>{key}:</strong> {value?.toString() || ''}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Right Column: Link Details */}
      {selectedLink && (
        <div className="col-12 col-md-4 p-3 node-details-column">
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Relationship Details</h6>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSelectedLink(null)}
              >
                ×
              </button>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <h5 className="card-title mb-2">
                  {selectedLink.type || 'CONNECTED'}
                </h5>

                {/* Source and Target Information */}
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <strong className="me-2">From:</strong>
                    <span>
                      {(() => {
                        const sourceNode = typeof selectedLink.source === 'object'
                          ? selectedLink.source
                          : displayData.nodes?.find(n => n.id === selectedLink.source);
                        return sourceNode?.name || sourceNode?.id || 'Unknown';
                      })()}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <strong className="me-2">To:</strong>
                    <span>
                      {(() => {
                        const targetNode = typeof selectedLink.target === 'object'
                          ? selectedLink.target
                          : displayData.nodes?.find(n => n.id === selectedLink.target);
                        return targetNode?.name || targetNode?.id || 'Unknown';
                      })()}
                    </span>
                  </div>
                </div>

                {/* Link Properties */}
                {selectedLink.properties && Object.keys(selectedLink.properties).length > 0 && (
                  <div className="mb-3">
                    <strong>Properties:</strong>
                    <ul className="list-unstyled mt-2">
                      {Object.entries(selectedLink.properties).map(([key, value]) => (
                        <li key={key} className="mb-1">
                          <small>
                            <strong>{key}:</strong> {String(value)}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Link Description */}
                {selectedLink.description && (
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <p className="mt-1 mb-0">{selectedLink.description}</p>
                  </div>
                )}

                {/* Link ID */}
                <div className="text-muted">
                  <small>ID: {selectedLink.id || 'N/A'}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphView;